import React from "react";
import { Document, Page, Text, View, StyleSheet, Image, pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import QRCode from "qrcode";
import logo from '../../business/assets/img/ade-logo-square.png';
import config from '../../business/config.json';

// Create styles
const getStyles = (grayscale = false) => StyleSheet.create({
  page: {
    padding: 30,
    backgroundColor: '#ffffff',
    color: grayscale ? '#000000' : '#ffffff',
    fontFamily: 'Helvetica',
  },
  container: {
    border: '5pt solid #000000',
    backgroundColor: grayscale ? '#ffffff' : '#1890ff',
    padding: 20,
  },
  header: {
    fontSize: 24,
    marginBottom: 10,
    borderBottom: grayscale ? '1pt solid #000000' : '1pt solid #ffffff',
    paddingBottom: 10,
    fontWeight: 'bold',
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    paddingVertical: 10,
    borderBottom: grayscale ? '1pt solid #000000' : '1pt solid #ffffff',
  },
  label: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  value: {
    fontSize: 12,
    textAlign: 'right',
  },
  reportSection: {
    marginTop: 20,
    alignItems: 'center',
  },
  reportTitle: {
    fontSize: 18,
    alignSelf: 'flex-start',
    marginBottom: 10,
  },
  qrContainer: {
    backgroundColor: '#ffffff',
    padding: 10,
    borderRadius: 8,
    border: grayscale ? '1pt solid #000000' : 'none',
  },
  qrCode: {
    width: 200,
    height: 200,
  },
  footer: {
    marginTop: 20,
    paddingVertical: 10,
    borderTop: grayscale ? '1pt solid #000000' : '1pt solid #ffffff',
  },
  footerTitle: {
    fontSize: 18,
    marginBottom: 10,
  },
  footerContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logo: {
    width: 80,
    marginRight: 20,
  },
  companyInfo: {
    flex: 1,
    alignItems: 'flex-end',
  },
  companyName: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  companyLink: {
    fontSize: 12,
    color: grayscale ? '#000000' : '#ffffff',
    textDecoration: 'underline',
  },
});

// Create Document Component
const QRCodePosterDocument = ({ project, qrCodeDataUrl, grayscale = false }) => {
  const styles = getStyles(grayscale);
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          <Text style={styles.header}>HAZMAT Building Report</Text>

          <View style={styles.row}>
            <Text style={styles.label}>Place ({project.ID || project.id_project})</Text>
            <Text style={styles.value}>{project.place_project}</Text>
          </View>

          <View style={styles.row}>
            <Text style={styles.label}>Address</Text>
            <View style={{ textAlign: 'right' }}>
              <Text style={styles.value}>{project.street_project}</Text>
              <Text style={styles.value}>{project.city_project}</Text>
              <Text style={styles.value}>{project.name_region} {project.postcode_project}</Text>
            </View>
          </View>

          <View style={styles.reportSection}>
            <Text style={styles.reportTitle}>Report</Text>
            <View style={styles.qrContainer}>
              {qrCodeDataUrl && <Image style={styles.qrCode} src={qrCodeDataUrl} />}
            </View>
          </View>

          <View style={styles.footer}>
            <Text style={styles.footerTitle}>Inspected By</Text>
            <View style={styles.footerContent}>
              <Image
                style={styles.logo}
                src={logo}
              />
              <View style={styles.companyInfo}>
                <Text style={styles.companyName}>ADE Consulting Group</Text>
                <Text style={styles.companyLink}>https://ade.group</Text>
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

const generateQRCodePosterBlob = async (project, grayscale = false) => {
  try {
    console.log(project)
    const url = `${config.app_domain}/dashboard/docs/contractor-download/${project.ID || project.id_project}`;
    const qrCodeDataUrl = await QRCode.toDataURL(url, {
      width: 512,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#ffffff'
      }
    });

    return await pdf(<QRCodePosterDocument project={project} qrCodeDataUrl={qrCodeDataUrl} grayscale={grayscale} />).toBlob();
  } catch (error) {
    console.error('Failed to generate PDF blob', error);
    throw error;
  }
};

const downloadQRCodePoster = async (project, grayscale = false) => {
  try {
    const blob = await generateQRCodePosterBlob(project, grayscale);
    saveAs(blob, `QR_Poster_${project.ID || project.id_project}${grayscale ? '_Grayscale' : ''}.pdf`);
  } catch (error) {
    console.error('Failed to download PDF', error);
  }
};

export { QRCodePosterDocument, downloadQRCodePoster, generateQRCodePosterBlob };
